import {ProjectId, UserId} from '@webmate-web-core/shared/types/ids';

export const WORKBENCH_IPC_SCHEMA_VERSION = 1;

export const WORKBENCH_IPC_HEARTBEAT_INTERVAL_MS = 2000;
export const WORKBENCH_IPC_REGISTRY_FRESHNESS_MS = 5000;
export const WORKBENCH_IPC_HOST_QUERY_TIMEOUT_MS = 300;

export type WorkbenchIpcSenderRole = 'webmate' | 'workbench' | 'popout';

export type WorkbenchMode = 'default' | 'manual-test' | 'audit';

export type WorkbenchIpcMessageType =
    'workbench:announce'
    | 'workbench:heartbeat'
    | 'workbench:query'
    | 'workbench:claim'
    | 'workbench:focus'
    | 'workbench:open'
    | 'workbench:mode-state'
    | 'workbench:set-mode'
    | 'workbench:popout-sync';

export interface WorkbenchIpcEnvelope<T extends WorkbenchIpcMessageType = WorkbenchIpcMessageType, P = unknown> {
    schemaVersion: typeof WORKBENCH_IPC_SCHEMA_VERSION;
    messageId: string;
    senderId: string;
    senderRole: WorkbenchIpcSenderRole;
    userId?: UserId;
    timestamp: number;
    projectId?: ProjectId;
    correlationId?: string;
    type: T;
    payload: P;
}

export interface WorkbenchPresencePayload {
    /**
     * Stable identity of the workbench browser tab. In the browser adapter this currently matches senderId;
     * it remains a separate field so future adapters can distinguish transport sender identity from tab identity.
     */
    tabId: string;
    createdAt: number;
    ready: boolean;
    activeMode: WorkbenchMode;
    /**
     * The workbench's current document URL. Diagnostics only — focusing resolves the tab by name and
     * deliberately never navigates it.
     */
    currentUrl?: string;
    /**
     * The browsing-context name the workbench has set on its window. Requesters pass this to
     * `focusWorkbenchWindowByName` to re-acquire a Window reference after their own page refresh.
     */
    windowName?: string;
}

export interface WorkbenchQueryPayload {
    requestedAt: number;
}

export interface WorkbenchClaimPayload {
    tabId: string;
    createdAt: number;
    ready: boolean;
    activeMode: WorkbenchMode;
    currentUrl?: string;
    windowName?: string;
}

export interface WorkbenchFocusPayload {
    targetSenderId?: string;
    startupAction?: WorkbenchStartupActionPayload;
}

export interface WorkbenchOpenPayload {
    startupAction?: WorkbenchStartupActionPayload;
}

export interface WorkbenchModeStatePayload {
    activeMode?: WorkbenchMode;
    requestedAt?: number;
}

export interface WorkbenchSetModePayload {
    activeMode: WorkbenchMode;
    startupAction?: WorkbenchStartupActionPayload;
}

export interface WorkbenchStartupActionPayload {
    kind: string;
    params?: unknown;
}

export type WorkbenchPopoutSyncPayload = {
    syncType: 'update-context';
    fromChild?: boolean;
    contextPatch: Record<string, unknown>;
} | {
    syncType: 'execute-command';
    fromChild?: boolean;
    target?: string;
    commandId: string;
    params?: unknown;
};

export type WorkbenchIpcPayloadMap = {
    'workbench:announce': WorkbenchPresencePayload;
    'workbench:heartbeat': WorkbenchPresencePayload;
    'workbench:query': WorkbenchQueryPayload;
    'workbench:claim': WorkbenchClaimPayload;
    'workbench:focus': WorkbenchFocusPayload;
    'workbench:open': WorkbenchOpenPayload;
    'workbench:mode-state': WorkbenchModeStatePayload;
    'workbench:set-mode': WorkbenchSetModePayload;
    'workbench:popout-sync': WorkbenchPopoutSyncPayload;
};

export type WorkbenchIpcMessage<T extends WorkbenchIpcMessageType = WorkbenchIpcMessageType> =
    WorkbenchIpcEnvelope<T, WorkbenchIpcPayloadMap[T]>;

export interface WorkbenchIpcSendOptions {
    projectId?: ProjectId;
    correlationId?: string;
}

export interface WorkbenchPresenceLocation {
    currentUrl?: string;
    windowName?: string;
}

export interface WorkbenchRegistryEntry {
    projectId: ProjectId;
    tabId: string;
    senderId: string;
    createdAt: number;
    lastSeenAt: number;
    ready: boolean;
    activeMode: WorkbenchMode;
    currentUrl?: string;
    windowName?: string;
}

export type WorkbenchPresenceMessage =
    WorkbenchIpcMessage<'workbench:announce' | 'workbench:heartbeat' | 'workbench:claim'>;

export function isWorkbenchPresenceMessage(message: WorkbenchIpcMessage): message is WorkbenchPresenceMessage {
    return message.senderRole === 'workbench'
        && !!message.projectId
        && (
            message.type === 'workbench:announce'
            || message.type === 'workbench:heartbeat'
            || message.type === 'workbench:claim'
        );
}

export function workbenchRegistryEntryFromMessage(message: WorkbenchPresenceMessage, lastSeenAt = Date.now()): WorkbenchRegistryEntry {
    return {
        projectId: message.projectId!,
        tabId: message.payload.tabId,
        senderId: message.senderId,
        createdAt: message.payload.createdAt,
        lastSeenAt,
        ready: message.payload.ready,
        activeMode: message.payload.activeMode,
        currentUrl: message.payload.currentUrl,
        windowName: message.payload.windowName
    };
}
